/**
 * A record as one document: who it is about, where, the terms, its lines, and where it comes from (the runs that
 * stored its documents). Every value carries its sources and age as quiet mono text; values the systems disagree on
 * sit under the main one with a wavy mark; a "how fresh" chooser fades everything older than the choice. The data is
 * the cloud's card, loaded exactly as pages/Data.tsx loads it for a record window today (q = the key, focus = its
 * data model, plus the stage's time params).
 */
import { usePeekParam } from './usePeekView.ts';
import { ScrollX } from '../stage/ScrollX.tsx';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import type { Card, CardField, CardSection } from '../lib/api.ts';
import { Api } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';
import { useAsync } from '../lib/hooks.ts';
import { useNow } from '../lib/asof.tsx';
import { compactTime, count } from '../lib/format.ts';
import { stripSystem } from '../lib/systems.ts';
import { dayWord } from '../stage/answer.ts';
import { RunIcon } from '../components/icons.tsx';
import { Kicker, NounIcon, Pill, SystemBadge, cx, noun } from '../design/primitives.tsx';
import type { MessageRef } from './usePeek.ts';
import { DocChip, KickerRow } from './RunPeek.tsx';
import { isIdentifier } from './fields.ts';

/** Quoted for the query syntax when it has spaces, quotes or parentheses, as pages/Data.tsx does. */
const quoted = (v: string) => (/[\s"()]/.test(v) ? `"${v.replace(/"/g, '')}"` : v);
const plural = (n: number, word: string) => `${count(n)} ${n === 1 ? word : pluralize(word)}`;
/** The last word made plural: "invoice row" → "invoice rows", "delivery" → "deliveries". */
function pluralize(label: string): string {
  return label.replace(/(\S+)$/, (w) => (/[^aeiou]y$/i.test(w) ? w.slice(0, -1) + 'ies' : /(s|x|ch|sh)$/i.test(w) ? w + 'es' : w + 's'));
}
/**
 * "2 h", "3 d": how long ago, short, bucketed once: an age rounds to the unit it is labelled in, so 23.6 h is
 * "1 d" like 24.2 h, never "24 h" beside it (lib/freshness.ts's age() rounds hours and days apart).
 */
export function age(ms: number, now: number): string {
  const s = Math.max(0, (now - ms) / 1000);
  const min = Math.round(s / 60);
  const h = Math.round(s / 3600);
  if (s < 59.5) return `${Math.round(s)} s`;
  if (min < 60) return `${min} min`;
  if (h < 24) return `${h} h`;
  return `${Math.round(s / 86400)} d`;
}
/** The short word for a field in a change note: "street, postcode, city". */
const SHORT: Record<string, string> = {
  street: 'street', postcode: 'postcode', city: 'city', country: 'country', name: 'name', businessId: 'business id', account: 'customer number',
  email: 'email', phone: 'phone', owner: 'owner', creditLimit: 'credit limit', paymentTerms: 'payment terms',
};
const shortLabel = (f: CardField) => (f.family && SHORT[f.family]) || f.label.toLowerCase();

/** Columns compared between systems in a lines table: quantities with quantities, prices with prices. */
const KINDS: [string, string, RegExp][] = [['qty', 'quantities', /(qty|quantity|count)/i], ['price', 'prices', /price/i], ['amount', 'amounts', /(amount|total|sum)/i]];
const kindOf = (label: string) => KINDS.find(([, , re]) => re.test(label))?.[0] ?? null;
const num = (v: string) => Number(v.replace(/\s/g, '').replace(',', '.'));

type Values = CardField['values'];
const lastOf = (v: Values[number]) => Math.max(0, ...v.sources.map((x) => x.at ?? 0));
/** The value most systems have first, then the newest. */
const ordered = (f: CardField) => [...f.values].sort((a, b) => b.sources.length - a.sources.length || lastOf(b) - lastOf(a));

export function RecordPeek({ cls, recordKey, time, onMessage, onRun, onStory }: {
  cls: string;
  /** The record's key (an order number); `key` is JSX's own, hence the name. */
  recordKey: string;
  /** The stage's time params (from, to), carried to the card as pages/Data.tsx does. */
  time: URLSearchParams;
  onMessage: (ref: MessageRef) => void;
  onRun: (executionId: string) => void;
  onStory: () => void;
}) {
  const { tenant } = useAuth();
  const tid = tenant?.id ?? '';
  const res = useAsync(() => {
    const p = new URLSearchParams(time);
    p.set('q', quoted(recordKey));
    p.set('focus', cls);
    return Api.card(tid, p);
  }, [tid, cls, recordKey, time.toString()]);
  const c = res.data;

  // Ages are as of when the card arrived, so the choices do not drift while it is open
  const live = useNow();
  const now = useMemo(() => live, [c]); // eslint-disable-line react-hooks/exhaustive-deps
  const [chosen, setChosen] = usePeekParam('pfresh');
  const ages = useMemo(() => agesOf(c, now), [c, now]);
  const since = ages.find((a) => a.label === chosen)?.oldest ?? null;
  const runs = useMemo(() => runsOf(c), [c]);
  // When each data model last wrote: the age of a lines table's columns, which have no sources of their own
  const docAt = useMemo(() => {
    const m = new Map<string, number>();
    for (const x of c?.messages ?? []) m.set(x.cls, Math.max(m.get(x.cls) ?? 0, x.at));
    return m;
  }, [c]);

  if (res.error) return <p className="m-0 pt-2 text-small text-fail">{res.error}</p>;

  return (
    <article className="flex flex-col gap-6 pt-2">
      <header className="flex flex-col gap-2">
        <h2 className="m-0 font-mono text-title font-medium tracking-tight [overflow-wrap:anywhere]">{recordKey}</h2>
        {c ? <Facts c={c} now={now} /> : <span className="h-4 w-72 rounded-chip mv-shimmer" />}
        {c && c.partial && <p className="m-0 text-micro text-warn">Some vaults did not answer; the record may have more.</p>}
      </header>

      {!c && <Skeleton />}

      {c && ages.length > 1 && (
        <div className="flex items-center gap-3 flex-wrap">
          <Kicker>How fresh</Kicker>
          <span className="flex flex-wrap bg-n3 rounded-full p-0.5 gap-0.5" role="radiogroup" aria-label="Only values sent since">
            <Pill active={chosen === null} onClick={() => setChosen(null)}>any</Pill>
            {ages.map((a) => (
              <Pill key={a.label} active={chosen === a.label} onClick={() => setChosen(chosen === a.label ? null : a.label)}>
                <span className="font-mono">{a.label}</span>
              </Pill>
            ))}
          </span>
        </div>
      )}
      {c && ages.length === 1 && <p className="m-0 text-small text-n8">Every value was sent <span className="font-mono text-n9">{ages[0].label}</span> ago.</p>}

      {c && c.sections.length === 0 && <p className="m-0 text-small text-n8">No fields were found for this record.</p>}
      {c && c.sections.map((s, i) => <Section key={`${s.id}|${i}`} s={s} since={since} now={now} index={i} docAt={docAt} />)}

      {c && (
        <section className="flex flex-col gap-3">
          <KickerRow title="Where it comes from" fact={`${plural(runs.length, 'run')} · ${plural(c.messages.length, 'document')}`} />
          {runs.length === 0 && <p className="m-0 text-small text-n8">No documents were found for this record.</p>}
          {runs.length > 0 && (
            <ol className="m-0 p-0 list-none flex flex-col">
              {runs.map((r, i) => (
                <li key={r.id} className="border-t border-n4 py-2.5 flex flex-col gap-1.5 mv-rise" style={{ animationDelay: `${Math.min(i, 12) * 25}ms` }}>
                  <button type="button" onClick={() => r.executionId && onRun(r.executionId)} disabled={!r.executionId}
                    className="group flex items-center gap-2.5 w-full text-left bg-transparent border-0 p-0 cursor-pointer disabled:cursor-default">
                    <NounIcon kind="run" size={16} />
                    <span className="flex-1 min-w-0 truncate text-small text-n10 transition-colors duration-[var(--t-micro)] group-hover:text-accent group-disabled:group-hover:text-n10">
                      {stripSystem(r.process) || 'a run'}
                    </span>
                    {r.failed > 0 && <span className="font-mono text-micro text-fail whitespace-nowrap">{count(r.failed)} failed</span>}
                    <span className="font-mono text-micro text-n7 whitespace-nowrap">{compactTime(r.first)}</span>
                  </button>
                  <div className="flex flex-wrap gap-1.5 pl-[26px]">
                    {r.docs.map((m) => <DocChip key={m.uuid} cls={m.cls} status={m.status} onClick={() => onMessage({ vaultId: m.vaultId, uuid: m.uuid, day: m.day })} />)}
                  </div>
                </li>
              ))}
            </ol>
          )}
          {c.messages.length > 0 && (
            <button type="button" onClick={onStory}
              className="self-start inline-flex items-center gap-2 h-9 pl-3 pr-4 rounded-full bg-accent text-n2 text-small font-semibold border-0 cursor-pointer transition-opacity duration-[var(--t-micro)] hover:opacity-90">
              <RunIcon size={16} />Tell the story
            </button>
          )}
        </section>
      )}
    </article>
  );
}

// ---------------------------------------------------------------- derived from the card

/** "18 documents · 8 runs · first yesterday 09:12 · last seen 2 min ago" (the day words of the answer: today, yesterday, Mon, 5 Oct). */
function Facts({ c, now }: { c: Card; now: number }) {
  const docs = c.messages.length;
  if (docs === 0) return <p className="m-0 text-small text-n8">No documents in this time range.</p>;
  const runs = new Set(c.messages.map((m) => m.executionId).filter(Boolean)).size;
  const first = Math.min(...c.messages.map((m) => m.at));
  const last = Math.max(...c.messages.map((m) => m.at));
  const systems = [...new Set(c.messages.map((m) => m.system))];
  return (
    <div className="flex flex-col gap-2">
      <p className="m-0 text-small text-n8">
        {plural(docs, 'document')} · {plural(runs, 'run')} · first {dayWord(first, now)} <span className="font-mono text-n9">{dayjs(first).format('HH:mm')}</span> · last seen{' '}
        <span className="text-n9">{age(last, now)}</span> ago
      </p>
      <div className="flex flex-wrap gap-1">{systems.map((sys) => <SystemBadge key={sys} code={sys} size={20} />)}</div>
    </div>
  );
}

interface Age { label: string; oldest: number; newest: number; values: number }

/**
 * The ages of the values ("2 min", "2 d") worth choosing in "how fresh", newest first, five at most: each at least
 * a log step (twice the age) after the one before, so near-identical choices (3 h · 4 h · 6 h) collapse to the
 * ones that grey a different set; the oldest age is never a choice, since it would grey nothing.
 */
function agesOf(c: Card | undefined, now: number): Age[] {
  if (!c) return [];
  const byLabel = new Map<string, Age>();
  for (const s of c.sections) {
    for (const f of s.fields) {
      for (const v of f.values) {
        const at = lastOf(v);
        if (!at) continue;
        const label = age(at, now);
        const x = byLabel.get(label) ?? { label, oldest: at, newest: at, values: 0 };
        x.oldest = Math.min(x.oldest, at);
        x.newest = Math.max(x.newest, at);
        x.values++;
        byLabel.set(label, x);
      }
    }
  }
  const all = [...byLabel.values()].sort((a, b) => b.newest - a.newest);
  const out: Age[] = [];
  for (const a of all.slice(0, -1)) {
    const last = out.at(-1);
    if (!last || now - a.newest >= 2 * (now - last.newest)) out.push(a);
  }
  return out.slice(0, 5);
}

interface RunGroup { id: string; executionId: string | null; process: string | null; first: number; failed: number; docs: Card['messages'] }

/** The runs that stored the record's documents, oldest first (so the newest is last, where the story goes on). */
function runsOf(c: Card | undefined): RunGroup[] {
  if (!c) return [];
  const map = new Map<string, RunGroup>();
  for (const m of [...c.messages].sort((a, b) => a.at - b.at)) {
    const id = m.executionId ?? `doc:${m.uuid}`;
    const g = map.get(id) ?? { id, executionId: m.executionId, process: m.process, first: m.at, failed: 0, docs: [] };
    g.docs.push(m);
    g.first = Math.min(g.first, m.at);
    if (m.status === 'FAILED') g.failed++;
    map.set(id, g);
  }
  return [...map.values()].sort((a, b) => a.first - b.first);
}

interface Change { origin: string; fields: string[]; chain: string[]; at: number }

/**
 * Where the systems disagree because a value changed and spread: the newest value's earliest sender is where it
 * changed, the systems it reached are the chain, and the old value is still in the rest. Only told when the newest
 * value really came after the old one and reached more than one system.
 */
function changesOf(s: CardSection): Change[] {
  const byOrigin = new Map<string, Change>();
  for (const f of s.fields) {
    if (!f.conflict) continue;
    const vals = f.values.map((v) => ({ v, newest: lastOf(v), oldest: Math.min(...v.sources.map((x) => x.at ?? 0)) }));
    const current = vals.reduce((m, x) => (x.newest > m.newest ? x : m));
    const others = vals.filter((x) => x !== current);
    if (others.length === 0 || others.some((o) => o.newest >= current.oldest)) continue;
    const chain = [...new Set([...current.v.sources].sort((a, b) => a.at - b.at).map((x) => x.system))];
    if (chain.length < 2) continue;
    const c = byOrigin.get(chain[0]) ?? { origin: chain[0], fields: [], chain, at: current.oldest };
    c.fields.push(shortLabel(f));
    c.at = Math.min(c.at, current.oldest);
    byOrigin.set(chain[0], c);
  }
  return [...byOrigin.values()];
}

/** The section's kicker: what it is, and one fact about it ("WHO · 5 systems", "WHERE · 2 addresses"). */
function kickerOf(s: CardSection, now: number): { title: string; fact: string; warn: boolean } {
  const conflicts = s.fields.filter((f) => f.conflict).length;
  const systems = plural(s.systems.length, 'system');
  const differ = `${conflicts} differ${conflicts === 1 ? 's' : ''}`;
  const newest = Math.max(0, ...s.fields.flatMap((f) => f.values.map(lastOf)));
  switch (s.kind) {
    case 'party':
      return { title: 'Who', fact: conflicts ? `${systems} · ${differ}` : systems, warn: conflicts > 0 };
    case 'address': {
      const n = Math.max(1, ...s.fields.map((f) => f.values.length));
      return { title: s.id === 'delivery' ? 'Where to' : 'Where', fact: plural(n, 'address'), warn: n > 1 };
    }
    case 'terms': {
      const terms = s.fields.find((f) => f.family === 'paymentTerms') ?? s.fields[0];
      const v = terms?.values[0]?.value;
      return { title: 'Terms', fact: conflicts ? differ : v ?? systems, warn: conflicts > 0 };
    }
    case 'rows':
      return linesKicker(s);
    case 'record':
      return { title: noun(s.label), fact: conflicts ? differ : `${s.systems.join(' · ')}${newest ? ` · ${age(newest, now)}` : ''}`, warn: conflicts > 0 };
    case 'group':
      return { title: s.label.toLowerCase(), fact: conflicts ? differ : `${s.systems.join(' · ')}${newest ? ` · ${age(newest, now)}` : ''}`, warn: conflicts > 0 };
  }
}

/** "LINES · quantities agree in 2 systems", or "quantities differ in 1 line" when they do not. */
function linesKicker(s: CardSection): { title: string; fact: string; warn: boolean } {
  const t = s.table;
  const title = pluralize(s.label.toLowerCase());
  if (!t) return { title, fact: '', warn: false };
  const multi = s.systems.length > 1;
  const kinds = t.columns.map((col) => kindOf(col.label));
  const compared = KINDS.filter(([k]) => new Set(t.columns.filter((_, i) => kinds[i] === k).map((c) => c.system)).size > 1);
  if (!multi || compared.length === 0) return { title, fact: `${plural(t.rows.length, s.label.toLowerCase())} · ${s.systems.join(' · ')}`, warn: false };
  const [k, word] = compared[0];
  const differing = t.rows.filter((r) => {
    const vals = r.cells.map((v, i) => (kinds[i] === k && v !== null ? num(v) : null)).filter((v): v is number => v !== null && !Number.isNaN(v));
    return new Set(vals).size > 1;
  }).length;
  return differing > 0
    ? { title, fact: `${word} differ in ${plural(differing, s.label.toLowerCase())}`, warn: true }
    : { title, fact: `${word} agree in ${plural(s.systems.length, 'system')}`, warn: false };
}

// ---------------------------------------------------------------- sections

function Section({ s, since, now, index, docAt }: { s: CardSection; since: number | null; now: number; index: number; docAt: Map<string, number> }) {
  const k = kickerOf(s, now);
  const changes = useMemo(() => (s.kind === 'address' || s.kind === 'party' || s.kind === 'terms' ? changesOf(s) : []), [s]);
  return (
    <section className="mv-rise" style={{ animationDelay: `${Math.min(index, 8) * 30}ms` }}>
      <KickerRow title={k.title} fact={k.fact} warn={k.warn} />
      {s.fields.length > 0 && (
        <dl className="m-0 grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] gap-x-4">
          {s.fields.map((f) => <Field key={f.label} f={f} since={since} now={now} />)}
          {changes.map((ch) => (
            <div key={ch.origin} className="contents">
              <dt className="text-small text-n7 border-t border-n4 py-2 [overflow-wrap:anywhere]">changed in {ch.origin}</dt>
              <dd className="m-0 min-w-0 border-t border-n4 py-2 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <span className="text-small text-n10">{ch.fields.join(', ')}</span>
                <Sources systems={ch.chain.slice(0, 3).concat(ch.chain.length > 3 ? ['…'] : [])} at={ch.at} now={now} arrow />
              </dd>
            </div>
          ))}
        </dl>
      )}
      {s.table && <Table s={s} since={since} docAt={docAt} />}
    </section>
  );
}

function Field({ f, since, now }: { f: CardField; since: number | null; now: number }) {
  const values = ordered(f);
  return (
    <div className="contents">
      <dt className="text-small text-n7 border-t border-n4 py-2 [overflow-wrap:anywhere]">{f.label.toLowerCase()}</dt>
      <dd className="m-0 min-w-0 border-t border-n4 py-2 flex flex-col gap-1">
        {values.map((v, i) => {
          const last = lastOf(v);
          const systems = [...new Set(v.sources.map((x) => x.system))];
          const mono = isIdentifier(v.value);
          return (
            <div key={v.value} className={cx('flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 transition-opacity duration-[var(--t-std)] ease-mv',
              since !== null && last > 0 && last < since && 'peek-old')}>
              <span className={cx('min-w-0 [overflow-wrap:anywhere]', i === 0 ? 'text-n10' : 'text-n9 peek-differs', mono ? 'font-mono text-small' : i === 0 ? 'text-body' : 'text-small')}>
                {v.value}
              </span>
              <Sources systems={systems} at={last} now={now} />
            </div>
          );
        })}
      </dd>
    </div>
  );
}

/** Where a value is and how old it is, as quiet mono text: "CRM · ERP · FIN  2 d". */
function Sources({ systems, at, now, arrow }: { systems: string[]; at: number; now: number; arrow?: boolean }) {
  return (
    <span className="font-mono text-micro text-n7 whitespace-nowrap tracking-[.02em]">
      {systems.join(arrow ? ' → ' : ' · ')}
      {at > 0 && <span className="text-n6 ml-2">{age(at, now)}</span>}
    </span>
  );
}

/** The lines side by side per system; a column whose data model wrote before the "how fresh" choice fades like a value would. */
function Table({ s, since, docAt }: { s: CardSection; since: number | null; docAt: Map<string, number> }) {
  const t = s.table!;
  const multi = s.systems.length > 1;
  const kinds = t.columns.map((col) => kindOf(col.label));
  const old = t.columns.map((col) => {
    const at = docAt.get(col.cls) ?? 0;
    return since !== null && at > 0 && at < since;
  });
  const numeric = t.columns.map((_, i) => {
    const vs = t.rows.map((r) => r.cells[i]).filter((v): v is string => v !== null);
    return vs.length > 0 && vs.every((v) => !Number.isNaN(num(v)));
  });
  return (
    <ScrollX className="-mx-1 px-1">
      <table className="w-full border-collapse text-small">
        <thead>
          <tr>
            {t.key && <th className="text-left font-medium text-micro uppercase tracking-[.08em] text-n7 py-1.5 pr-3 border-b border-n4 whitespace-nowrap">{t.key.toLowerCase()}</th>}
            {t.columns.map((col, i) => (
              <th key={i} className={cx('font-medium text-micro uppercase tracking-[.08em] text-n7 py-1.5 pr-3 border-b border-n4 whitespace-nowrap transition-opacity duration-[var(--t-std)] ease-mv', numeric[i] ? 'text-right' : 'text-left', old[i] && 'peek-old')}>
                <span className={cx('inline-flex items-center gap-1.5', numeric[i] && 'flex-row-reverse')}>
                  {multi && <SystemBadge code={col.system} size={20} />}{col.label.toLowerCase()}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {t.rows.map((r) => {
            // Quantities, prices or amounts of the same line that differ between systems
            const differs = t.columns.map((col, i) => {
              const k = kinds[i];
              if (!k || r.cells[i] === null) return false;
              return t.columns.some((c, j) => kinds[j] === k && c.system !== col.system && r.cells[j] !== null && num(r.cells[j]!) !== num(r.cells[i]!));
            });
            return (
              <tr key={r.key}>
                {t.key && <td className="py-2 pr-3 border-b border-n3 font-mono text-n9 whitespace-nowrap">{r.key}</td>}
                {r.cells.map((v, i) => (
                  <td key={i} className={cx('py-2 pr-3 border-b border-n3 [overflow-wrap:anywhere] transition-opacity duration-[var(--t-std)] ease-mv', numeric[i] && 'text-right font-mono', v === null && 'text-n6',
                    differs[i] && 'text-warn font-medium peek-differs', old[i] && 'peek-old')}>
                    {v ?? '—'}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollX>
  );
}

/** The record's layout before its data: a chooser, three sections of rows, the runs. */
function Skeleton() {
  return (
    <div className="flex flex-col gap-6" aria-busy="true">
      <span className="h-7 w-64 rounded-full mv-shimmer" />
      {[4, 3, 3].map((rows, i) => (
        <div key={i} className="flex flex-col">
          <span className="h-3 w-28 rounded-chip mv-shimmer mb-2" />
          {Array.from({ length: rows }, (_, j) => (
            <div key={j} className="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)] gap-x-4 border-t border-n4 py-2.5">
              <span className="h-3.5 w-20 rounded-chip mv-shimmer" />
              <span className="h-3.5 rounded-chip mv-shimmer" style={{ width: `${55 + ((i + j) % 3) * 15}%` }} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
